import type { PanelTable } from '../../../queryV5/types';
import { readGraphTable } from './graphRows';
import type { GraphData, GraphEdge, GraphNode, GraphSettings } from './types';

export { DEFAULT_FIELDS, edgeTone, formatGraphValue } from './graphMetrics';

/** One aggregate row per scope/source/target. Never average percentiles or rates. */
export function buildGraph(
	tables: PanelTable[],
	settings: GraphSettings,
): GraphData {
	const nodes = new Map<string, GraphNode>();
	const edges = new Map<string, GraphEdge>();
	for (const table of tables) {
		const parsed = readGraphTable(table, settings);
		if (parsed.status === 'invalid') {
			return parsed;
		}
		for (const node of parsed.nodes) {
			nodes.set(node.id, node);
		}
		for (const edge of parsed.edges) {
			if (edges.has(edge.id)) {
				return {
					status: 'invalid',
					message:
						'Multiple rows describe the same connection. Group the query by scope, source and target.',
				};
			}
			edges.set(edge.id, edge);
		}
	}
	const maxNodes = Math.max(1, Math.min(200, settings.maxNodes ?? 80));
	const rankedEdges = [...edges.values()].sort(
		(a, b) => (b.calls ?? 0) - (a.calls ?? 0) || a.id.localeCompare(b.id),
	);
	const retainedNodes = new Set<string>();
	const retainedEdges: GraphEdge[] = [];
	for (const edge of rankedEdges) {
		const additions = new Set(
			[edge.source, edge.target].filter((id) => !retainedNodes.has(id)),
		);
		if (
			retainedNodes.size + additions.size > maxNodes ||
			retainedEdges.length >= 400
		) {
			continue;
		}
		retainedNodes.add(edge.source);
		retainedNodes.add(edge.target);
		retainedEdges.push(edge);
	}
	return {
		status: 'ready',
		nodes: [...nodes.values()].filter((node) => retainedNodes.has(node.id)),
		edges: retainedEdges,
		omitted: edges.size - retainedEdges.length,
	};
}
